/**
 * Browser client for our API proxy → Indusviva live API.
 * Auth token (x-auth) is cached in sessionStorage when Indusviva returns it.
 */
import { apiUrl } from './apiBase';

const AUTH_KEY = 'ileafs_x_auth';

export type ApiOk<T> = { ok: true; data: T; auth?: string };
export type ApiErr = { ok: false; message: string; data?: unknown; code?: string };

export type LiveProduct = {
  id: number;
  product_name: string;
  thumbnail_url: string;
  pv: number;
  selling_price: number;
  order_Sequence: number;
};

export type LiveLocation = {
  postalcode_id: number;
  postalcode: string;
  post_name: string;
  is_service_available: number;
  city_id: number;
  city_name: string;
  district_id: number;
  district_name: string;
  state_id: number;
  state_name: string;
  country_id?: number;
  country_name?: string;
};

export type PayuPayload = {
  key: string;
  txnid: string;
  productinfo: string;
  amount: string | number;
  email: string;
  firstname: string;
  surl: string;
  furl: string;
  phone: string;
  hash: string;
};

export type MyOrderRow = {
  order_id?: number | string;
  order_no?: string;
  order_type?: string;
  order_status?: string;
  order_status_id?: number;
  order_status_time?: string;
  item_list?: string;
  shippment_status_name?: string;
  amount?: number | string;
  price?: number | string;
  total_unit_quantity?: number;
  total_free_product_quantity?: number;
  PV?: number;
  week?: number;
  docket_no?: string | null;
  shipper_name?: string | null;
};

export type OrderDetailItem = {
  order_item_id?: number | string;
  product_name?: string;
  description?: string;
  quantity?: number | string;
  pv?: number | string;
  PV?: number | string;
  total_amount_pay?: number | string;
  amount?: number | string;
};

export type OrderDetailsData = {
  order_id?: number | string;
  order_no?: string;
  order_placed_time?: string;
  shipping_contact?: string;
  distributor_name?: string;
  distributor_id?: number | string;
  week_id?: number | string;
  delivery_mode?: string;
  warehouse_name?: string;
  shipper_name?: string | null;
  docket_no?: string | null;
  payment_status?: string;
  payment_time?: string | null;
  transaction_id?: string | null;
  total_amount_pay?: number | string;
  amount?: number | string;
  order_status?: string;
  order_status_updated_time?: string;
  shippment_status_name?: string;
  order_token?: string;
  order_items?: OrderDetailItem[];
  address?: {
    shipping?: {
      address_line1?: string;
      address_line2?: string;
      postalname?: string;
      city?: string;
      district?: string;
      state?: string;
      country?: string;
      postalcode?: string;
    };
  };
};

function getAuth() {
  return sessionStorage.getItem(AUTH_KEY) || localStorage.getItem(AUTH_KEY);
}

function persistAuth(token: string) {
  sessionStorage.setItem(AUTH_KEY, token);
  localStorage.setItem(AUTH_KEY, token);
}

function rememberAuth(res: Response, json?: ApiOk<unknown> | ApiErr) {
  const fromHeader = res.headers.get('x-auth');
  const fromBody = json && 'ok' in json && json.ok && typeof json.auth === 'string' ? json.auth : null;
  const auth = fromHeader || fromBody;
  if (auth) persistAuth(auth);
}

export function clearIndusvivaAuth() {
  sessionStorage.removeItem(AUTH_KEY);
  localStorage.removeItem(AUTH_KEY);
}

export function hasIndusvivaAuth() {
  return Boolean(getAuth());
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers || {});
  headers.set('Accept', 'application/json');
  if (init?.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const auth = getAuth();
  if (auth) headers.set('x-auth', auth);

  const res = await fetch(apiUrl(`/api/indusviva${path}`), { ...init, headers });
  const json = (await res.json()) as ApiOk<T> | ApiErr;
  rememberAuth(res, json);
  if (!res.ok || !json.ok) {
    throw new Error((json as ApiErr).message || 'Request failed');
  }
  return (json as ApiOk<T>).data;
}

export const indusvivaApi = {
  session: () =>
    api<{
      sellerId: string;
      resellerBadge: string;
      payuAction: string;
      eligibility: Record<string, unknown>;
    }>('/session'),

  locations: (pincode: string) =>
    api<LiveLocation[]>(`/locations?pincode=${encodeURIComponent(pincode)}`),

  products: (opts: { pincode?: string; postcodeId?: number | string }) => {
    const qs = new URLSearchParams();
    if (opts.postcodeId) qs.set('postcode_id', String(opts.postcodeId));
    if (opts.pincode) qs.set('pincode', opts.pincode);
    return api<{ postcode_id: number; products: LiveProduct[] }>(`/products?${qs}`);
  },

  orderSummary: (body: Record<string, unknown>) =>
    api<Record<string, unknown>>('/order-summary', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  sendOtp: (body: { phone: string }) =>
    api<Record<string, unknown> | null>('/auth/send-otp', {
      method: 'POST',
      body: JSON.stringify({ phone: body.phone }),
    }),

  /** Fails with DISTRIBUTOR_PHONE when number belongs to a verified partner/distributor. */
  phoneCheck: (body: { phone: string }) =>
    api<Record<string, unknown>>('/auth/phone-check', {
      method: 'POST',
      body: JSON.stringify({ phone: body.phone }),
    }),

  verifyOtp: (body: { phone: string; phone_otp: string }) =>
    api<string | number | Record<string, unknown> | null>('/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ phone: body.phone, phone_otp: body.phone_otp }),
    }),

  signup: (body: { name: string; phone: string; email?: string }) =>
    api<Record<string, unknown>>('/auth/signup', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  customerDetails: (body: { customer_user_id: string | number }) =>
    api<Record<string, unknown>>('/auth/customer-details', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  myOrders: (body: {
    customer_id: string | number;
    page_no?: number;
    order_no?: string;
    order_status?: string;
    order_time_id?: number;
    year?: number | null;
  }) =>
    api<{
      report?: MyOrderRow[];
      total_no_of_pages?: number;
    }>('/my-orders', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  orderDetails: (body: {
    customer_id: string | number;
    order_id?: string | number;
    order_no?: string;
  }) =>
    api<OrderDetailsData>('/order-details', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  checkout: (body: Record<string, unknown>) =>
    api<{
      order: Record<string, unknown>;
      payment: Record<string, unknown> | null;
      payu: PayuPayload | null;
      payuAction: string;
    }>('/checkout', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  payment: (body: Record<string, unknown>) =>
    api<{
      payment: Record<string, unknown>;
      payu: PayuPayload | null;
      payuAction: string;
    }>('/payment', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

/** Auto-submit hidden form to PayU (same as official Indusviva store). */
export function redirectToPayu(payu: PayuPayload, action = 'https://secure.payu.in/_payment') {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = action;
  const fields: (keyof PayuPayload)[] = [
    'key',
    'txnid',
    'productinfo',
    'amount',
    'email',
    'firstname',
    'surl',
    'furl',
    'phone',
    'hash',
  ];
  for (const name of fields) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = String(payu[name] ?? '');
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.submit();
}
